import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { YachtLogo } from './YachtLogo';
import { auth } from '../firebase';

const NavItem = ({ 
  title, 
  to, 
  subitems,
  activeDropdown,
  setActiveDropdown
}: { 
  title: string, 
  to: string, 
  subitems: { name: string, to: string, description: string }[],
  activeDropdown: string | null,
  setActiveDropdown: (title: string | null) => void
}) => (
  <div 
    className="relative group"
    onMouseEnter={() => setActiveDropdown(title)}
    onMouseLeave={() => setActiveDropdown(null)}
  >
    <Link 
      to={to} 
      className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5 py-4"
    >
      {title}
    </Link>
    
    <AnimatePresence>
      {activeDropdown === title && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute top-full left-1/2 -translate-x-1/2 w-64 bg-zinc-950 border border-white/10 rounded-2xl p-4 shadow-2xl backdrop-blur-xl z-[60]"
        >
          <div className="space-y-1">
            {subitems.map((item, idx) => (
              <Link 
                key={idx} 
                to={item.to}
                className="block p-3 rounded-xl hover:bg-white/5 transition-all group/item"
              >
                <p className="text-[10px] font-mono uppercase tracking-widest text-white mb-1">{item.name}</p>
                <p className="text-[9px] text-zinc-500 leading-tight">{item.description}</p>
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 px-4 md:px-12 py-4 flex items-center justify-between transition-all duration-500 border-b ${isScrolled || !isLandingPage ? 'bg-black/80 backdrop-blur-xl border-white/5' : 'bg-transparent border-transparent'}`}>
      <Link to="/" className="flex items-center gap-4 cursor-pointer">
        <YachtLogo className="w-6 h-6" />
        <span className="font-mono text-xl tracking-tighter uppercase whitespace-nowrap text-white">Yacht Labs</span>
      </Link>
      
      <div className="hidden lg:flex items-center gap-8 xl:gap-12">
        <NavItem 
          title="Product" 
          to="/product" 
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          subitems={[
            { name: "Synthesis", to: "/product/synthesis", description: "AI Architecture Engine" },
            { name: "Kinetics", to: "/product/kinetics", description: "Real-time Distribution" },
            { name: "Workbench", to: "/product/workbench", description: "Visual Editor & Design" }
          ]} 
        />
        <NavItem 
          title="Use Cases" 
          to="/use-cases" 
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          subitems={[
            { name: "Startups", to: "/use-cases/startups", description: "Validate & Ship Fast" },
            { name: "Enterprise", to: "/use-cases/enterprise", description: "Scalable Infrastructure" },
            { name: "Agencies", to: "/use-cases/agencies", description: "Deliver 10x Velocity" }
          ]} 
        />
        <NavItem 
          title="Solution" 
          to="/solutions" 
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          subitems={[
            { name: "GTM", to: "/solutions/gtm", description: "Market Entry Strategy" },
            { name: "Prototyping", to: "/solutions/prototyping", description: "High-Fidelity Concepts" },
            { name: "Deployment", to: "/solutions/deployment", description: "Edge Cloud Integration" }
          ]} 
        />
        <NavItem 
          title="Resources" 
          to="/resources" 
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          subitems={[
            { name: "Learning", to: "/resources/learning", description: "Tutorials & Guides" },
            { name: "API Docs", to: "/resources/api-reference", description: "Reference & SDKs" },
            { name: "Blueprints", to: "/resources/blueprints", description: "Community Templates" },
            { name: "Support", to: "/#support", description: "Get Help from our Crew" }
          ]} 
        />
        <NavItem 
          title="Company" 
          to="/about" 
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          subitems={[
            { name: "Team", to: "/company/team", description: "The Crew Behind Yacht" },
            { name: "Vision", to: "/company/vision", description: "Our Mission & Values" },
            { name: "Careers", to: "/company/careers", description: "Join the Future" }
          ]} 
        />
        <NavItem 
          title="Pricing" 
          to="/pricing" 
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          subitems={[
            { name: "Individual", to: "/pricing/individual", description: "Founder Tier" },
            { name: "Team", to: "/pricing/team", description: "Collaborative Labs" },
            { name: "Enterprise", to: "/pricing/enterprise", description: "Unlimited Scale" }
          ]} 
        />
      </div>

      <div className="flex items-center gap-4">
        {auth.currentUser ? (
          <Link to="/studio" className="px-5 py-2.5 bg-white text-black text-[10px] font-mono uppercase tracking-widest font-bold rounded-xl hover:bg-zinc-200 transition-all">
            Dashboard
          </Link>
        ) : (
          <>
            <Link 
              to="/login"
              className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors px-2"
            >
              Log In
            </Link>
            <Link 
              to="/signup"
              className="px-5 py-2.5 bg-[#0074D9] text-white text-[10px] font-mono uppercase tracking-widest font-bold rounded-xl hover:bg-[#0074D9]/80 shadow-[0_0_20px_rgba(0,116,217,0.3)] transition-all flex items-center gap-2"
            >
              Sign Up <ArrowRight size={12} />
            </Link>
          </>
        )}
      </div>
    </nav>
  );
}
